<template>
  <div class="boss-page">
    <header class="boss-header">
      <van-icon name="arrow-left" @click="$router.back()"/>
      <span>CRM数据</span>
      <button type="button" class="header-add-btn" @click="goAdd">新增</button>
    </header>

    <nav class="tabs">
      <span :class="{active: view==='quantity'}" @click="switchView('quantity')">客户数量</span>
      <span :class="{active: view==='value'}" @click="switchView('value')">客户价值</span>
      <span :class="{active: view==='cross'}" @click="switchView('cross')">交叉分析</span>
      <span :class="{active: view==='rank'}" @click="switchView('rank')">排行榜</span>
    </nav>

    <p class="data-cutoff" v-if="dataCutoff">数据截止:{{ dataCutoff }}</p>

    <!-- 客户数量 / 客户价值 -->
    <section v-if="view==='quantity' || view==='value'" class="panel">
      <h3 class="panel-title" @click="goCustomerList">
        <template v-if="view==='quantity'">
          客户总数:{{ total }}
          <small>(个人客户数:{{ personalCount }})【点击查看】</small>
        </template>
        <template v-else>
          总价值:{{ totalValue }}<small>万元</small>
          <small>(个人价值:{{ personalValue }}万元)【点击查看】</small>
        </template>
      </h3>

      <div class="cat-row">
        <div class="cat-label">{{ view === 'value' ? '按类别' : '按类别' }}</div>
        <div class="cat-list">
          <div class="cat-item" v-for="(c, i) in categories" :key="c.name">
            <div class="cat-circle" :style="{ background: categoryColor(i) }">
              <span v-if="view==='quantity'">{{ c.count }}</span>
              <span v-else>{{ c.value }}</span>
            </div>
            <div class="cat-name" :style="{ color: categoryColor(i) }">{{ c.name }}</div>
            <div class="cat-pct" v-if="view==='value'">{{ c.valuePercent }}%</div>
          </div>
        </div>
      </div>

      <div class="pie-block">
        <div class="block-label">{{ view === 'value' ? '按项目' : '按项目' }}</div>
        <div ref="industryPie" class="pie"></div>
      </div>

      <div class="bar-block">
        <div class="block-label">{{ view === 'value' ? '按销售(万元):' : '按销售(个):' }}</div>
        <div class="owner-stacked">
          <div class="stacked-bar">
            <div
              v-for="(o, idx) in owners"
              :key="o.name"
              class="stacked-segment"
              :style="{
                flex: getOwnerFlex(o),
                background: getOwnerColor(idx)
              }"
            >
              <span class="seg-num">{{ ownerValue(o) }}</span>
            </div>
          </div>
          <div class="stacked-labels">
            <div
              v-for="(o, idx) in owners"
              :key="o.name"
              class="stacked-label"
              :style="{ flex: getOwnerFlex(o), color: getOwnerColor(idx) }"
            >
              {{ o.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="line-card">
        <h4>各项目客户开发情况（按天·开发时间）</h4>
        <div ref="industryLine" class="line"></div>
      </div>
      <div class="line-card">
        <h4>各类别客户开发情况（按天·开发时间）</h4>
        <div ref="categoryLine" class="line"></div>
      </div>
      <div class="line-card">
        <h4>各销售客户开发情况（按天·开发时间）</h4>
        <div ref="ownerLine" class="line"></div>
      </div>
    </section>

    <!-- 交叉分析 -->
    <section v-else-if="view==='cross'" class="panel">
      <div class="pie-block">
        <div ref="categoryRing" class="pie ring"></div>
      </div>
      <div class="stacked-card">
        <h4>各项目-类别-客户数</h4>
        <div ref="industryCategoryBar" class="bar"></div>
      </div>
      <div class="stacked-card">
        <h4>各项目-类别-客户金额</h4>
        <div ref="industryCategoryValueBar" class="bar"></div>
      </div>
      <div class="stacked-card">
        <h4>各销售-类别-客户数</h4>
        <div ref="ownerCategoryBar" class="bar"></div>
      </div>
      <div class="stacked-card">
        <h4>各销售-类别-客户金额</h4>
        <div ref="ownerCategoryValueBar" class="bar"></div>
      </div>
      <div class="quadrant-card">
        <h4>项目四象限分析</h4>
        <div ref="industryQuadrant" class="quad"></div>
      </div>
      <div class="quadrant-card">
        <h4>销售四象限分析</h4>
        <div ref="ownerQuadrant" class="quad"></div>
      </div>
    </section>

    <!-- 排行榜 -->
    <section v-else class="panel rank">
      <div class="rank-banner">
        <div class="banner-title">销售风云榜</div>
        <div class="banner-sub">RANKING<br><small>SALES</small><br><small>REPRESENTATIVE</small></div>
        <div class="trophy">🏆</div>
      </div>
      <div class="rank-tab">中安集团荣耀时刻</div>

      <div class="rank-card" v-for="(group, gi) in rankGroups" :key="gi">
        <h4>{{ group.title }}</h4>
        <table>
          <thead>
          <tr>
            <th>排名</th>
            <th>{{ group.nameCol }}</th>
            <th>{{ group.totalCol }}</th>
            <th>S级</th>
            <th>A级</th>
            <th>B级</th>
            <th>C级</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in group.rows" :key="row.rank">
            <td>
              <span class="medal" v-if="row.rank<=3">🏅</span>
              <span v-else>{{ row.rank }}</span>
            </td>
            <td>{{ row.name }}</td>
            <td class="num">{{ row.total }}</td>
            <td class="num">{{ row.S }}</td>
            <td class="num">{{ row.A }}</td>
            <td class="num">{{ row.B }}</td>
            <td class="num">{{ row.C }}</td>
          </tr>
          </tbody>
        </table>
        <p class="more" @click="$router.push({ path: '/boss/customer-rank', query: { type: group.type } })">查看完整榜单&gt;&gt;</p>
      </div>
    </section>
  </div>
</template>

<script>
import coreapi from "@/utils/coreapi";

const CAT_COLORS = [
  "#ee5b4f", // S类
  "#f5a623", // A类
  "#28a55e", // B类
  "#3b8ee0", // C类
  "#9c6ade", // H类
  "#17b3a3", // N类
  "#e0457b", // 其他1
  "#7a8290"  // 其他2
];
const INDUSTRY_COLORS = {
  "安全事业部": "#ee5b4f",
  "安全": "#ee5b4f",
  "传媒事业部": "#3b8ee0",
  "传媒": "#3b8ee0",
  "文化事业部": "#f5a623",
  "文化": "#f5a623",
  "健康事业部": "#28a55e",
  "健康": "#28a55e"
};
// 行业兜底调色板：排除已固定行业色（红/蓝/橙/绿）相近色，避免撞色
const INDUSTRY_PALETTE = [
  "#9c6ade", "#17b3a3", "#e0457b", "#7a8290", "#d4a017",
  "#8e44ad", "#1abc9c", "#e91e63", "#795548", "#607d8b",
  "#56ccf2", "#6c5ce7", "#00b894", "#fdcb6e", "#a29bfe"
];

// 按名称哈希选择兜底色，保证同行业颜色稳定且与排列顺序无关
function getIndustryColor(name, index) {
  if (INDUSTRY_COLORS[name]) return INDUSTRY_COLORS[name];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % INDUSTRY_PALETTE.length;
  return INDUSTRY_PALETTE[idx];
}

export default {
  name: "BossCustomerData",
  data() {
    return {
      view: "quantity",
      data: {},
      dataCutoff: "",
      // CRM 用户 id（对应 crm_customer.creator），来自登录缓存；140 表示全部
      creator: localStorage.getItem("CrmUserId") || ""
    };
  },
  computed: {
    total() {
      return this.data.total || 0;
    },
    totalValue() {
      return Number(this.data.totalValue || 0).toFixed(2);
    },
    personalCount() {
      return this.data.personalCount || 0;
    },
    personalValue() {
      return Number(this.data.personalValue || 0).toFixed(2);
    },
    categories() {
      return (this.data.categories || []).filter(c => c.name && c.name !== "其他");
    },
    industries() {
      return this.data.industries || [];
    },
    owners() {
      return (this.data.owners || []);
    },
    rankGroups() {
      return this.buildRankGroups();
    }
  },
  mounted() {
    this.dataCutoff = this.formatCutoff(new Date());
    this.load(this.view);
  },
  beforeRouteLeave(to, from, next) {
    this.disposeCharts();
    next();
  },
  beforeDestroy() {
    this.disposeCharts();
  },
  methods: {
    getOwnerColor(idx) {
      // 直接沿用类目调色板，确保与四象限/其它模块颜色一致
      return CAT_COLORS[idx % CAT_COLORS.length];
    },
    getOwnerFlex(o) {
      // flex 比例 = 该负责人 数量 / 所有者 最大数量；最大者设最小基数确保窄段也可点
      const val = this.view === "value" ? Number(o.value) : Number(o.count);
      return val > 0 ? val : 1;
    },
    ownerValue(o) {
      // 按当前 tab 决定展示：客户数量视图显示客户数量，客户价值视图显示金额（单位见 block-label）
      if (this.view === "value") {
        const v = Number(o.value);
        return v.toFixed(2);
      }
      return o.count;
    },
    switchView(v) {
      if (v === this.view) return;
      this.view = v;
      this.load(v);
    },
    goCustomerList() {
      this.$router.push({
        path: "/boss/customer-list",
        query: {type: this.view === "value" ? "value" : "count"}
      });
    },
    goAdd() {
      this.$router.push({path: "/boss/customer-add", query: {from: "list", type: "count"}});
    },
    formatCutoff(d) {
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
    },
    async load(view) {
      this.disposeCharts();
      try {
        // Boss 端「客户数据」为全量汇总页，不按当前销售过滤（creator 传空=全部）；
        // 同时带上 me=CrmUserId 以统计「个人客户数/个人客户价值」
        const data = await coreapi.bossCustomerData(view, "", this.creator);
        this.data = data || {};
      } catch (e) {
        this.data = {};
        this.$toast && this.$toast(e.message || "数据加载失败");
      }
      this.$nextTick(() => this.render());
    },
    disposeCharts() {
      ["industryPie", "industryLine", "categoryLine", "ownerLine",
        "categoryRing", "industryCategoryBar", "industryCategoryValueBar",
        "ownerCategoryBar", "ownerCategoryValueBar", "industryQuadrant",
        "ownerQuadrant"].forEach(k => {
        const c = this.$refs[k] && this.$refs[k].__chart;
        if (c) {
          c.dispose();
        }
      });
    },
    initChart(ref) {
      if (!this.$refs[ref]) return null;
      const el = this.$refs[ref];
      const chart = this.$echarts.init(el);
      el.__chart = chart;
      return chart;
    },
    render() {
      if (this.view === "quantity" || this.view === "value") this.renderMain();
      else if (this.view === "cross") this.renderCross();
      else this.renderRank();
    },
    renderMain() {
      this.renderIndustryPie();
      this.renderCategoryLine();
      this.renderIndustryLine();
      this.renderOwnerLine();
    },
    renderIndustryPie() {
      const chart = this.initChart("industryPie");
      if (!chart) return;
      const data = this.industries.map((it, i) => ({
        name: it.name,
        value: this.view === "value" ? Number(it.value) : it.count
      }));
      const total = data.reduce((s, d) => s + d.value, 0);
      chart.setOption({
        tooltip: {trigger: "item", formatter: "{b}: {c} ({d}%)"},
        legend: {show: false},
        color: data.map((d, i) => getIndustryColor(d.name, i)),
        series: [{
          type: "pie",
          radius: ["38%", "70%"],
          center: ["50%", "55%"],
          label: {
            formatter: "{b}\n{c}，{d}%",
            color: "#333",
            fontSize: 11
          },
          labelLine: {length: 8, length2: 8},
          data
        }]
      });
      // 用作无数据兜底
      if (total === 0) {
        chart.setOption({
          title: {
            text: "暂无数据", left: "center", top: "center",
            textStyle: {color: "#bbb", fontSize: 12}
          }
        });
      }
    },
    renderLineChart(ref, daily, valueMode) {
      const chart = this.initChart(ref);
      if (!chart) return;
      const days = (daily && daily.days) || [];
      const series = (daily && daily.series) || [];
      // 仅有一天时，折线无法直观展示数据，自动切换为柱状图并显示数值
      const singleDay = days.length === 1;
      chart.setOption({
        grid: {top: 48, left: 46, right: 12, bottom: 28},
        tooltip: {trigger: "axis"},
        legend: {top: 0, left: 0, itemWidth: 10, itemHeight: 6, textStyle: {fontSize: 10}},
        xAxis: {
          type: "category", data: days,
          axisLine: {lineStyle: {color: "#cfcfcf"}},
          axisLabel: {fontSize: 10, color: "#999"}
        },
        yAxis: {
          type: "value",
          name: valueMode ? "客户价值(万元)" : "数量",
          nameTextStyle: {color: "#999", fontSize: 10},
          minInterval: 1,
          splitLine: {lineStyle: {type: "dashed", color: "#eee"}},
          axisLabel: {
            color: "#999", fontSize: 10,
            formatter: (v) => valueMode ? Number(v).toFixed(2) : Math.round(v)
          }
        },
        color: CAT_COLORS.concat(["#3b8ee0", "#9b59b6", "#1abc9c"]),
        series: series.map(s => ({
          name: s.name,
          type: singleDay ? "bar" : "line",
          smooth: !singleDay,
          data: (s.data || []).map(v => valueMode ? Number(v) || 0 : Math.round(Number(v) || 0)),
          symbol: singleDay ? "circle" : "none",
          label: singleDay
            ? {
              show: true, position: "top", fontSize: 10, color: "#666",
              formatter: valueMode ? (p) => Number(p.value).toFixed(2) : undefined
            }
            : {show: false},
          lineStyle: singleDay ? undefined : {width: 1.5}
        }))
      });
    },
    renderIndustryLine() {
      this.renderLineChart("industryLine", this.data.industryDaily, this.view === "value");
    },
    renderCategoryLine() {
      this.renderLineChart("categoryLine", this.data.categoryDaily, this.view === "value");
    },
    renderOwnerLine() {
      this.renderLineChart("ownerLine", this.data.ownerDaily, this.view === "value");
    },
    renderCross() {
      this.renderCategoryRing();
      this.renderCrossStackedBar("industryCategoryBar", this.data.industries, "count");
      this.renderCrossStackedBar("industryCategoryValueBar", this.data.industries, "value");
      this.renderCrossStackedBar("ownerCategoryBar", this.data.owners, "count");
      this.renderCrossStackedBar("ownerCategoryValueBar", this.data.owners, "value");
      this.renderQuadrant("industryQuadrant", "industries");
      this.renderQuadrant("ownerQuadrant", "owners");
    },
    renderCategoryRing() {
      const chart = this.initChart("categoryRing");
      if (!chart) return;
      // 南丁格尔玫瑰图：每片扇区半径按数据值缩放，中心留出空白圆
      const raw = this.categories.filter(c => Number(c.count || 0) > 0);
      const max = raw.reduce((m, c) => Math.max(m, Number(c.count || 0)), 0);
      const minR = 12, maxR = 60; // 每个扇区的百分比半径范围
      const list = raw.map((c, i) => {
        const v = Number(c.count || 0);
        const r = max > 0 ? minR + (maxR - minR) * (v / max) : minR;
        return {
          name: c.name,
          value: v,
          val: Number(c.value || 0),
          rawColor: CAT_COLORS[i % CAT_COLORS.length],
          _r: r
        };
      });
      chart.setOption({
        tooltip: {
          trigger: "item",
          formatter: p => {
            const it = list[p.dataIndex];
            return `${it.name}<br/>数量：${it.value}<br/>价值：${it.val}`;
          }
        },
        legend: {
          bottom: 0, left: "center",
          itemWidth: 10, itemHeight: 6,
          textStyle: {fontSize: 11, color: "#666"},
          data: list.map(d => d.name)
        },
        color: list.map(d => d.rawColor),
        series: [{
          type: "pie",
          roseType: "area",
          radius: ["18%", "65%"],
          center: ["50%", "42%"],
          itemStyle: {borderRadius: 4},
          label: {
            show: true,
            formatter: p => {
              const it = list[p.dataIndex] || {};
              const hex = it.rawColor || "#333";
              return `{name|${it.name}}\n{sub|${it.value || 0}个，${(Number(it.val) || 0).toFixed(2)}w}`;
            },
            rich: {
              name: {color: "#333", fontSize: 13, fontWeight: 600, lineHeight: 18},
              sub: {color: "#999", fontSize: 11, lineHeight: 16}
            }
          },
          labelLine: {length: 10, length2: 16},
          data: list.map(d => ({
            name: d.name,
            value: d.value,
            itemStyle: {color: d.rawColor}
          }))
        }]
      });
    },
    renderCrossStackedBar(ref, rows, metric) {
      const el = this.$refs[ref];
      if (!el || !rows || !rows.length) return;
      // 高度按数据行数自适应：每行约 32px，上下留白约 50px
      const rowHeight = 32;
      const baseHeight = 50;
      const dynamicHeight = Math.max(110, rows.length * rowHeight + baseHeight);
      el.style.height = dynamicHeight + "px";

      const chart = this.initChart(ref);
      if (!chart) return;
      const cats = this.categories.map(c => c.name);
      const yNames = rows.map(it => it.name);
      const series = cats.map((cat, idx) => ({
        name: cat,
        type: "bar",
        stack: "total",
        barWidth: 14,
        barGap: "20%",
        itemStyle: {
          color: CAT_COLORS[idx % CAT_COLORS.length],
          borderRadius: [0, 2, 2, 0]
        },
        label: {
          show: true,
          position: "inside",
          color: "#fff",
          fontSize: 10,
          formatter: p => Number(p.value || 0) === 0 ? "" : p.value
        },
        data: rows.map(it => {
          const byCat = it.byCategory || {};
          if (metric === "value") {
            const total = Number(it.value || 0);
            const catCount = Number(byCat[cat] || 0);
            const totalCount = Number(it.count || 0);
            return totalCount > 0 ? Number((total * catCount / totalCount).toFixed(2)) : 0;
          }
          return Number(byCat[cat] || 0);
        })
      }));
      chart.setOption({
        grid: {top: 30, left: 56, right: 30, bottom: 28},
        tooltip: {trigger: "axis", axisPointer: {type: "shadow"}},
        legend: {top: 0, left: 0, itemWidth: 10, itemHeight: 6, textStyle: {fontSize: 10}},
        xAxis: {
          type: "value",
          name: metric === "value" ? "价值(万元)" : "数量",
          nameTextStyle: {color: "#999", fontSize: 10},
          minInterval: 1,
          axisLabel: {color: "#999", fontSize: 10},
          splitLine: {lineStyle: {color: "#f0f0f0"}}
        },
        yAxis: {
          type: "category",
          data: yNames,
          axisLabel: {color: "#ee5b4f", fontSize: 11},
          axisLine: {lineStyle: {color: "#cfcfcf"}},
          splitLine: {show: false}
        },
        series
      });
    },
    renderQuadrant(ref, key) {
      const chart = this.initChart(ref);
      if (!chart) return;
      const list = (this.data[key] || []);
      const CENTER_X = 10;  // 中心点 - 客户数量
      const CENTER_Y = 100; // 中心点 - 客户价值(万元)

      const data = list.map((it, i) => ({
        name: it.name,
        value: [it.count || 0, Number(it.value || 0)], // x=数量, y=价值(万元)
        count: it.count || 0,
        val: Number(it.value || 0),
        color: CAT_COLORS[i % CAT_COLORS.length]
      }));

      const xMax = Math.max(20, ...data.map(d => d.value[0])) * 1.1;
      const yMax = Math.max(200, ...data.map(d => d.value[1])) * 1.1;

      const QUAD_COLORS = {
        TL: "rgba(207, 215, 234, 0.45)", // 左上 低数量+高价值
        TR: "rgba(255, 196, 196, 0.45)",  // 右上 高数量+高价值
        BL: "rgba(208, 234, 207, 0.45)", // 左下 低数量+低价值
        BR: "rgba(255, 222, 178, 0.45)"   // 右下 高数量+低价值
      };

      chart.setOption({
        grid: {
          left: 50, right: 30, top: 30, bottom: 50,
          containLabel: false
        },
        legend: {
          type: "scroll",
          top: 0,
          left: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 10,
          textStyle: {color: "#666", fontSize: 11},
          data: data.map(d => d.name)
        },
        tooltip: {
          trigger: "item",
          formatter: p => `${p.data.name}<br/>客户数量：${p.data.count}<br/>客户价值：${p.data.val}万元`
        },
        xAxis: {
          type: "value",
          name: "客户数量",
          nameLocation: "end",
          nameGap: 18,
          nameTextStyle: {color: "#999", fontSize: 11},
          min: 0,
          max: xMax,
          scale: false,
          splitLine: {show: false},
          axisLine: {show: true, lineStyle: {color: "#999"}},
          axisTick: {show: false},
          axisLabel: {show: false}
        },
        yAxis: {
          type: "value",
          name: "客户价值(万元)",
          nameLocation: "end",
          nameGap: 18,
          nameTextStyle: {color: "#999", fontSize: 11},
          min: 0,
          max: yMax,
          scale: false,
          splitLine: {show: false},
          axisLine: {show: true, lineStyle: {color: "#999"}},
          axisTick: {show: false},
          axisLabel: {show: false}
        },
        series: [
          // 象限背景 + 中心十字
          {
            type: "scatter",
            data: [{value: [0, 0]}],
            symbolSize: 0,
            silent: true,
            markArea: {
              silent: true,
              itemStyle: {opacity: 1},
              data: [
                // 左上：x < CENTER_X, y > CENTER_Y
                [
                  {xAxis: 0, yAxis: yMax, itemStyle: {color: QUAD_COLORS.TL}},
                  {xAxis: CENTER_X, yAxis: CENTER_Y, itemStyle: {color: QUAD_COLORS.TL}}
                ],
                // 右上：x > CENTER_X, y > CENTER_Y
                [
                  {xAxis: CENTER_X, yAxis: yMax, itemStyle: {color: QUAD_COLORS.TR}},
                  {xAxis: xMax, yAxis: CENTER_Y, itemStyle: {color: QUAD_COLORS.TR}}
                ],
                // 左下：x < CENTER_X, y < CENTER_Y
                [
                  {xAxis: 0, yAxis: CENTER_Y, itemStyle: {color: QUAD_COLORS.BL}},
                  {xAxis: CENTER_X, yAxis: 0, itemStyle: {color: QUAD_COLORS.BL}}
                ],
                // 右下：x > CENTER_X, y < CENTER_Y
                [
                  {xAxis: CENTER_X, yAxis: CENTER_Y, itemStyle: {color: QUAD_COLORS.BR}},
                  {xAxis: xMax, yAxis: 0, itemStyle: {color: QUAD_COLORS.BR}}
                ]
              ]
            },
            markLine: {
              silent: true,
              symbol: "none",
              label: {show: false},
              lineStyle: {color: "#444", type: "solid", width: 1},
              data: [
                {xAxis: CENTER_X},
                {yAxis: CENTER_Y}
              ]
            }
          },
          // 实际散点（每个点独立 series，便于图例区分）
          ...data.map(d => ({
            type: "scatter",
            name: d.name,
            symbolSize: Math.min(80, Math.max(28, d.value[0] * 2 + 20)),
            data: [{
              name: d.name,
              value: d.value,
              count: d.count,
              val: d.val
            }],
            itemStyle: {
              color: d.color,
              opacity: 0.9,
              borderColor: "#fff",
              borderWidth: 2
            },
            label: {
              show: true,
              position: "inside",
              formatter: d.name,
              color: "#fff",
              fontSize: 11,
              fontWeight: 600
            }
          })),
          // 气泡上方"数量 / 价值"标注
          {
            type: "scatter",
            data: [],
            symbolSize: 0,
            silent: true,
            markPoint: {
              symbol: "roundRect",
              symbolSize: [70, 22],
              symbolOffset: [28, 0],
              itemStyle: {color: "rgba(255,255,255,0.5)", borderColor: "rgba(221,221,221,0.6)", borderWidth: 1},
              label: {
                show: true,
                position: "inside",
                color: "#ee5b4f",
                fontSize: 10,
                lineHeight: 12,
                formatter: p => {
                  return `{n|${p.data.count}个}\n{v|${p.data.val}万元}`;
                },
                rich: {
                  n: {color: "#ee5b4f", fontSize: 10, fontWeight: 600},
                  v: {color: "#ee5b4f", fontSize: 10}
                }
              },
              data: data.map(d => ({
                name: d.name,
                coord: [d.value[0], d.value[1]],
                count: d.count,
                val: d.val
              }))
            }
          }
        ]
      });

      const xPx = (x) => chart.convertToPixel({xAxisIndex: 0}, x);
      const yPx = (y) => chart.convertToPixel({yAxisIndex: 0}, y);

      const quadLabels = [
        {text: ["低数量", "高价值"], x: xPx((0 + CENTER_X) / 2), y: yPx((CENTER_Y + yMax) / 2), color: "#7a8fb8"},
        {text: ["高数量", "高价值"], x: xPx((CENTER_X + xMax) / 2), y: yPx((CENTER_Y + yMax) / 2), color: "#d97a7a"},
        {text: ["低数量", "低价值"], x: xPx((0 + CENTER_X) / 2), y: yPx((0 + CENTER_Y) / 2), color: "#7aaf7a"},
        {text: ["高数量", "低价值"], x: xPx((CENTER_X + xMax) / 2), y: yPx((0 + CENTER_Y) / 2), color: "#c89a5a"}
      ];

      const graphicEls = [];
      const xTickLabels = [5, 10, 15, 20, 25, 30];
      xTickLabels.forEach(val => {
        if (val > xMax) return;
        graphicEls.push({
          type: "text",
          position: [xPx(val), yPx(0) + 6],
          style: {text: String(val), fill: "#999", font: "11px sans-serif", textAlign: "center"}
        });
      });
      const yTickLabels = [100, 120, 140, 160, 180, 200, 220, 240];
      yTickLabels.forEach(val => {
        if (val > yMax) return;
        graphicEls.push({
          type: "text",
          position: [xPx(0) - 8, yPx(val)],
          style: {text: String(val), fill: "#999", font: "11px sans-serif", textAlign: "right", textVerticalAlign: "middle"}
        });
      });

      // 象限标题文字
      quadLabels.forEach(q => {
        q.text.forEach((line, i) => {
          graphicEls.push({
            type: "text",
            position: [q.x, q.y + (i === 0 ? -10 : 14)],
            style: {
              text: line,
              fill: q.color,
              font: "14px sans-serif",
              textAlign: "center",
              textVerticalAlign: "middle",
              opacity: 0.7
            }
          });
        });
      });

      chart.setOption({graphic: graphicEls});
    },
    renderRank() {
      // 排行榜是 DOM 表格，无需 echarts
    },
    buildRankGroups() {
      const ownerCount = this.data.ownerCountTop || [];
      const ownerValue = this.data.ownerValueTop || [];
      const indCount = this.data.industryCountTop || [];
      const indValue = this.data.industryValueTop || [];
      return [
        this.buildRankRow("客户开发数量排名TOP5", "姓名", "总量", ownerCount, false, "count"),
        this.buildRankRow("客户价值排名TOP5", "姓名", "价值(元)", ownerValue, true, "value"),
        this.buildRankRow("项目客户数量排名", "名称", "总量", indCount, false, "count"),
        this.buildRankRow("项目客户价值排名", "名称", "价值(元)", indValue, true, "value")
      ];
    },
    buildRankRow(title, nameCol, totalCol, list, isValue, type) {
      const rows = list.map(r => ({
        rank: r.rank,
        name: r.name,
        total: isValue ? Number(r.value || 0).toFixed(2) : (r.count || 0),
        S: r.s != null ? r.s : "—", A: r.a != null ? r.a : "—",
        B: r.b != null ? r.b : "—", C: r.c != null ? r.c : "—"
      }));
      return {title, nameCol, totalCol, rows, type};
    },
    categoryColor(i) {
      return CAT_COLORS[i % CAT_COLORS.length];
    }
  }
};
</script>

<style scoped>
.boss-page {
  min-height: 100vh;
  background: #f3f4f8;
  color: #303133;
  padding-bottom: 24px;
}

.boss-header {
  height: 52px;
  background: linear-gradient(90deg, #26191b, #c9272c);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  font-size: 18px;
  position: sticky;
  top: 0;
  z-index: 5;
}

.boss-header i {
  width: 24px;
}

.header-add-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.85);
  color: #fff;
  font-size: 14px;
  line-height: 1;
  padding: 5px 12px;
  border-radius: 14px;
  white-space: nowrap;
}

.header-add-btn:active {
  background: rgba(255, 255, 255, 0.18);
}

.tabs {
  display: flex;
  background: #fff;
  padding: 0 12px;
  border-bottom: 1px solid #eee;
}

.tabs span {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 14px;
  color: #666;
  position: relative;
}

.tabs span.active {
  color: #c9272c;
  font-weight: 600;
}

.tabs span.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 30%;
  right: 30%;
  height: 2px;
  background: #c9272c;
  border-radius: 2px;
}

.data-cutoff {
  text-align: right;
  padding: 6px 14px 0;
  color: #ee5b4f;
  font-size: 11px;
  margin: 0;
}

.panel {
  padding: 12px 12px 0;
}

.panel-title {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  margin: 6px 0 12px;
  cursor: pointer;
}

.panel-title small {
  font-size: 12px;
  color: #999;
  margin-left: 4px;
  font-weight: 400;
}

.cat-row {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.cat-label {
  color: #999;
  font-size: 12px;
  margin-bottom: 8px;
}

.cat-list {
  display: flex;
  justify-content: space-between;
}

.cat-item {
  text-align: center;
}

.cat-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px;
}

.cat-name {
  font-size: 12px;
}

.cat-pct {
  font-size: 10px;
  color: #999;
  margin-top: 2px;
}

.pie-block, .bar-block, .line-card, .stacked-card, .quadrant-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.pie-block .block-label, .bar-block .block-label {
  color: #999;
  font-size: 12px;
  margin-bottom: 6px;
}

.pie {
  height: 220px;
}

.ring {
  height: 260px;
}

.bar {
  height: 110px;
}

.stacked-card .bar {
  height: auto;
  min-height: 110px;
}

.owner-stacked {
  padding: 8px 0 6px;
}

.owner-stacked .stacked-bar {
  display: flex;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6fa;
}

.owner-stacked .stacked-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s ease;
}

.owner-stacked .stacked-segment + .stacked-segment {
  border-left: 2px solid #fff;
}

.owner-stacked .stacked-labels {
  display: flex;
  width: 100%;
  margin-top: 10px;
}

.owner-stacked .stacked-label {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 52px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  writing-mode: vertical-rl;
  letter-spacing: 1px;
}

.line {
  height: 220px;
}

.line-tip {
  font-size: 12px;
  color: #888;
  margin: -4px 0 12px;
  line-height: 1.5;
}

.line-tip strong {
  color: #555;
}

.quad {
  height: 280px;
}

.line-card h4, .stacked-card h4, .quadrant-card h4 {
  text-align: center;
  font-size: 14px;
  color: #333;
  margin: 0 0 8px;
}

/* 排行榜 */
.rank {
  background: linear-gradient(180deg, #fdf6e3 0%, #f5e6c5 100%);
}

.rank-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px;
  background: linear-gradient(90deg, #f5d57a, #b88a3a);
  color: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  position: relative;
}

.banner-title {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 4px;
  text-shadow: 0 2px 0 rgba(0, 0, 0, .1);
}

.banner-sub {
  font-size: 8px;
  color: #fff8;
  text-align: center;
}

.trophy {
  font-size: 40px;
}

.rank-tab {
  text-align: center;
  padding: 8px 0;
  color: #b88a3a;
  font-weight: 600;
  background: #fff8e6;
  border-radius: 22px;
  width: 60%;
  margin: -10px auto 12px;
  border: 1px solid #d4af37;
}

.rank-card {
  background: #fff8e6;
  border: 1px solid #d4af37;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.rank-card h4 {
  text-align: center;
  font-size: 13px;
  color: #b88a3a;
  margin: 0 0 8px;
  background: #f5e6c5;
  padding: 6px 0;
  border-radius: 4px;
}

.rank-card table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rank-card th {
  background: #f5e6c5;
  color: #b88a3a;
  padding: 6px 4px;
}

.rank-card td {
  padding: 8px 4px;
  text-align: center;
  border-top: 1px dashed #d4af37;
}

.rank-card td.num {
  color: #ee5b4f;
  font-weight: 600;
}

.rank-card .medal {
  font-size: 18px;
}

.more {
  text-align: center;
  color: #1989fa;
  font-size: 11px;
  margin: 6px 0 0;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/CustomerData.vue